import React, { useContext } from "react";
import FilterPopup from "../FilterPopup";
import { Button } from "semantic-ui-react";
import Region from "./AddressFilters/Region";
import Country from "./AddressFilters/Country";
import State from "./AddressFilters/State";
import City from "./AddressFilters/City";
import PostalCode from "./AddressFilters/PostalCode";
import ButtonGroup from "components/Atoms/ButtonGroup";
import { type AddressFilterType, getSortedCountryLabels, getSortedRegionLabels, getSortedStatesLabels, initialAddressFilter } from "./FilterTypes/AddressFilterType";
import { focusOnMount } from "utilities/reactUtils";
import { FilterContext } from "../FilterContext";
import { areArrayValuesEqual, areEqual } from "../FilterGroups/instructionUtils";
import { isNullOrEmptyString } from "shared/utils";

const areAddressesEqual = (a: AddressFilterType, b: AddressFilterType) => {
  return a.filterType === b.filterType &&
    areEqual(a.city, b.city, "") &&
    areEqual(a.postalCode, b.postalCode, "") &&
    areArrayValuesEqual(a.countries.map(c => c.key), b.countries.map(c => c.key)) &&
    areArrayValuesEqual(a.states.map(s => s.key), b.states.map(s => s.key)) &&
    areArrayValuesEqual(a.regions.map(r => r.value), b.regions.map(r => r.value));
};

const AddressFilter = () => {
  const title = RESX.Address.resxAddress;
  const applyRef = React.useRef<any>(null);

  const { register, getValue, setValue } = useContext(FilterContext);
  register("address", {
    getTitle: (filter) => getFilterData(filter).title,
    renderSelection: (filter) => <>{getFilterData(filter).textToDisplay}</>,
    hasValue: (filter) => {
      return (filter.filterType === "city" && filter.city.trim() !== "") ||
        (filter.filterType === "postalCode" && filter.postalCode.trim() !== "") ||
        (filter.filterType === "region" && filter.regions.length > 0) ||
        (filter.filterType === "state" && filter.states.length > 0) ||
        (filter.filterType === "country" && filter.countries.length > 0);
    },
    reset: () => initialAddressFilter,
    areEqual: areAddressesEqual
  });

  const [filterType, setFilterType] = React.useState<server.dto.AddressFilterTypes>(initialAddressFilter.filterType === "unknown" ? "region" : initialAddressFilter.filterType);
  const [regions, setRegions] = React.useState<server.dto.Region[]>(initialAddressFilter.regions);
  const [countries, setCountries] = React.useState<server.dto.Country[]>(initialAddressFilter.countries);
  const [states, setStates] = React.useState<server.dto.State[]>(initialAddressFilter.states);
  const [city, setCity] = React.useState<string>(initialAddressFilter.city ?? "");
  const [postalCode, setPostalCode] = React.useState<string>(initialAddressFilter.postalCode ?? "");

  const [popupOpen, setPopupOpen] = React.useState<boolean>(false);

  const onPopupClose = () => {
    onApply();
  };

  const onPopupOpen = () => {
    const value = getValue("address");
    setFilterType(value.filterType === "unknown" ? "region" : value.filterType);
    setRegions(value.regions);
    setCountries(value.countries);
    setStates(value.states);
    setCity(value.city ?? "");
    setPostalCode(value.postalCode ?? "");
    setPopupOpen(true);
  };

  const onFilterTypeSelected = (filterType: server.dto.AddressFilterTypes) => {
    setFilterType(filterType);
  };

  const onRegionToggled = (region: server.dto.Region) => {
    if (regions.some(r => r.value === region.value)) {
      const newRegions = regions.filter(r => r.value !== region.value);
      setRegions(newRegions);
    } else {
      const newRegions = [...regions, region];
      setRegions(newRegions);
    }
  };

  const onCountryAdded = (country: server.dto.Country) => {
    const newCountries = [...countries, country];
    setCountries(newCountries);
    // applyRef?.current?.focus();
  };

  const onCountryRemoved = (country: server.dto.Country) => {
    const newCountries = countries.filter(c => c.key !== country.key);
    setCountries(newCountries);
  };

  const onApply = (e?) => {
    e?.preventDefault();
    const hasAnyValue = (filterType === "city" && !isNullOrEmptyString(city)) ||
      (filterType === "postalCode" && !isNullOrEmptyString(postalCode)) ||
      (filterType === "region" && regions.length > 0) ||
      (filterType === "state" && states.length > 0) ||
      (filterType === "country" && countries.length > 0);

    const newFilter: AddressFilterType = {
      regions: filterType === "region" ? regions : [],
      states: filterType === "state" ? states : [],
      countries: filterType === "country" ? countries : [],
      postalCode: filterType === "postalCode" ? postalCode : "",
      city: filterType === "city" ? city : "",
      filterType: hasAnyValue ? filterType : "unknown"
    };

    setValue("address", newFilter);
    setPopupOpen(false);
  };

  return <div>
    <FilterPopup
      filterButtonLabel={title}
      content={<>
        <div className="margin-bottom">
          <ButtonGroup.Group>
            <ButtonGroup.Button
              group="addressFilterType"
              type="secondary"
              onClick={() => { onFilterTypeSelected("region"); }}
              active={filterType === "region"}
              otherProps={{
                ref: focusOnMount
              }}
            >
              {RESX.Region.resxRegion}
            </ButtonGroup.Button>
            <ButtonGroup.Button
              group="addressFilterType"
              type="secondary"
              onClick={() => { onFilterTypeSelected("country"); }}
              active={filterType === "country"}
            >
              {RESX.Address.Country}
            </ButtonGroup.Button>
            <ButtonGroup.Button
              group="addressFilterType"
              type="secondary"
              onClick={() => { onFilterTypeSelected("state"); }}
              active={filterType === "state"}
            >
              {RESX.Address.State}
            </ButtonGroup.Button>
            <ButtonGroup.Button
              group="addressFilterType"
              type="secondary"
              onClick={() => { onFilterTypeSelected("city"); }}
              active={filterType === "city"}
            >
              {RESX.Address.City}
            </ButtonGroup.Button>
            <ButtonGroup.Button
              group="addressFilterType"
              type="secondary"
              onClick={() => { onFilterTypeSelected("postalCode"); }}
              active={filterType === "postalCode"}
            >
              {RESX.Address.PostalCode}
            </ButtonGroup.Button>
          </ButtonGroup.Group>
        </div>
        <div className="margin-bottom">
          {filterType === "region" && <Region value={regions} onToggle={onRegionToggled} />}
          {filterType === "country" && <Country value={countries} onAdd={onCountryAdded} onRemove={onCountryRemoved} />}
          {filterType === "state" && <State value={states} onChanged={setStates} />}
          {filterType === "city" && <City value={city} onChange={setCity} />}
          {filterType === "postalCode" && <PostalCode value={postalCode} onChange={setPostalCode} />}
        </div>
        <Button ref={applyRef} fluid onClick={onApply} primary>
          {RESX.SelectionManager.Apply}
        </Button>
      </>}
      width={720}
      open={popupOpen}
      onClose={onPopupClose}
      onOpen={onPopupOpen}
    />
  </div>;
};

const getFilterData = (filter: AddressFilterType): {
  title: string
  textToDisplay: string
} => {
  switch (filter.filterType) {
    case "unknown":
      return {
        title: "Unknown",
        textToDisplay: "Unknown"
      };
    case "region":
      return {
        title: RESX.Region.resxRegion,
        textToDisplay: getSortedRegionLabels(filter).join(", ")
      };
    case "country":
      return {
        title: RESX.Address.Country,
        textToDisplay: getSortedCountryLabels(filter).join(", ")
      };
    case "state":
      return {
        title: RESX.Address.State,
        textToDisplay: getSortedStatesLabels(filter).join(", ")
      };
    case "city":
      return {
        title: RESX.Address.City,
        textToDisplay: filter.city
      };
    case "postalCode":
      return {
        title: RESX.Address.PostalCode,
        textToDisplay: filter.postalCode
      };
  }
};

export default AddressFilter;
